import { Control, useController } from 'react-hook-form';
import { GraniteSelect } from '../Select/Select';
import { useQuery } from 'react-query';
import { getAllRoles } from 'screens/RolesPermissions/utils';
import { usePermissions } from 'hooks/usePermissions';
import { getTextForSystemRole } from 'screens/RolesPermissions/CustomRoles/utils';
import { useMemo } from 'react';

export type FormWithPermissions = {
  roles: { value: string; label: string }[];
};

export interface UserPermissionFeatureSwitchesProps<
  FormType extends FormWithPermissions,
> {
  control: FormType extends FormWithPermissions ? Control<FormType> : never;
  isProfile?: boolean;
  isMulti?: boolean;
}

interface RoleError {
  roles?: {
    message?: string;
    type?: string;
  };
}

export const UserRolePermissionFeatureSwitches = <
  FormType extends FormWithPermissions,
>({
  control,
  isProfile,
  isMulti,
}: UserPermissionFeatureSwitchesProps<FormType>) => {
  const {
    field: { ref, ...role },
    fieldState: { error: roleErrors },
  } = useController({ control, name: 'roles' });
  const { isSuperAdmin } = usePermissions();

  const { data, isLoading } = useQuery(
    ['user-form-all-roles'],
    () => getAllRoles(),
    {
      refetchOnWindowFocus: false,
      enabled: !isProfile,
    },
  );
  const roles = useMemo(() => {
    return data?.map((r) => ({
      value: r.name,
      label: getTextForSystemRole(r.name),
    }));
  }, [data]);

  return (
    <div className="flex w-full flex-col">
      <h2 className="mb-4 text-2xl font-semibold text-content-base-default">
        Role & permissions
      </h2>
      <GraniteSelect
        {...role}
        onChange={(newValue) => {
          role.onChange(isSuperAdmin ? newValue : [newValue]);
        }}
        label="Role"
        options={roles}
        error={(roleErrors as RoleError)?.roles?.message ?? roleErrors?.message}
        isDisabled={isProfile || isLoading}
        isMulti={isSuperAdmin || isMulti}
      />
      <p className="mt-2 text-sm text-content-base-subdued">
        {role.value?.[0]?.value === 'viewer' &&
          'This user will be limited to read only permissions'}
        {role.value?.[0]?.value === 'user' &&
          'This user can have read & write or read only permissions'}
        {role.value?.[0]?.value === 'company-admin' &&
          'This user will have full read & write permissions and the ability to manage users'}
      </p>
    </div>
  );
};
